import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import config from 'config'
import { AddFlowControlDeviceConfigRequest } from 'pages/settings/configuration/ManageFlowControlDevicesScreen'
import { useAuth0Authentication } from './useAuth0Authentication'

export interface FlowControlDeviceConfig {
    id: string,
    type: string,
    size: number,
	manufacturer?: string,
}

export interface IUseFlowControlDevices {
    flowControlDevices: FlowControlDeviceConfig[] | undefined,
    isLoading: boolean,
    error: Error | null,
    addFlowControlDevice: (flowControlDevice: AddFlowControlDeviceConfigRequest) => Promise<void>,
    isAdding: boolean,
    addError: Error | null,
    editFlowControlDevice: (flowControlDevice: FlowControlDeviceConfig) => Promise<void>,
    isEditing: boolean,
    editError: Error | null,
    deleteFlowControlDevice: (flowControlDevice: FlowControlDeviceConfig) => Promise<void>,
    isDeleting: boolean,
    deleteError: Error | null
}

const fetchFlowControlDevices = async (accessToken: string): Promise<FlowControlDeviceConfig[]> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/flow-control-devices`,
        {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to fetch flow control devices: ${response.statusText}`)
    }

    const flowControlDevicesResp: FlowControlDeviceConfig[] = await response.json()
    return flowControlDevicesResp.sort((a, b) => a.type.localeCompare(b.type) || a.size - b.size)
}

// Separate API function for adding flow control devices
const addFlowControlDeviceAPI = async (accessToken: string, flowControlDevice: AddFlowControlDeviceConfigRequest): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/flow-control-devices`,
        {
            method: 'POST',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(flowControlDevice)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to add flow control device: ${response.statusText}`)
    }
}

// Separate API function for editing flow control devices
const editFlowControlDeviceAPI = async (accessToken: string, flowControlDevice: FlowControlDeviceConfig): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/flow-control-devices`,
        {
            method: 'PATCH',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(flowControlDevice)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to edit flow control device: ${response.statusText}`)
    }
}

// Separate API function for deleting flow control devices
const deleteFlowControlDeviceAPI = async (accessToken: string, flowControlDevice: FlowControlDeviceConfig): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/flow-control-devices`,
        {
            method: 'DELETE',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(flowControlDevice)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to delete flow control device: ${response.statusText}`)
    }
}

export const useFlowControlDevices = (): IUseFlowControlDevices => {
    const { getAuth0AccessToken } = useAuth0Authentication()
    const queryClient = useQueryClient()

    const {
        data: flowControlDevices,
        isLoading,
        error
    } = useQuery<FlowControlDeviceConfig[], Error>({
        queryKey: ['flowControlDevices'],
        queryFn: async () => {
            const accessToken = await getAuth0AccessToken()
            return fetchFlowControlDevices(accessToken)
        }
    })

    const {
        mutateAsync: addFlowControlDevice,
        isPending: isAdding,
        error: addError
    } = useMutation<void, Error, FlowControlDeviceConfig>({
        mutationFn: async (flowControlDevice: AddFlowControlDeviceConfigRequest) => {
            const accessToken = await getAuth0AccessToken()
            return addFlowControlDeviceAPI(accessToken, flowControlDevice)
        },
        onSuccess: () => {
            // Invalidate and refetch flow control devices after successful addition
            queryClient.invalidateQueries({ queryKey: ['flowControlDevices'] })
        }
    })

    const {
        mutateAsync: editFlowControlDevice,
        isPending: isEditing,
        error: editError
    } = useMutation<void, Error, FlowControlDeviceConfig>({
        mutationFn: async (flowControlDevice: FlowControlDeviceConfig) => {
            const accessToken = await getAuth0AccessToken()
            return editFlowControlDeviceAPI(accessToken, flowControlDevice)
        },
        onSuccess: () => {
            // Invalidate and refetch flow control devices after successful edit
            queryClient.invalidateQueries({ queryKey: ['flowControlDevices'] })
        }
    })

    const {
        mutateAsync: deleteFlowControlDevice,
        isPending: isDeleting,
        error: deleteError
    } = useMutation<void, Error, FlowControlDeviceConfig>({
        mutationFn: async (flowControlDevice: FlowControlDeviceConfig) => {
            const accessToken = await getAuth0AccessToken()
            return deleteFlowControlDeviceAPI(accessToken, flowControlDevice)
        },
        onSuccess: () => {
            // Invalidate and refetch flow control devices after successful deletion
            queryClient.invalidateQueries({ queryKey: ['flowControlDevices'] })
        }
    })

    return {
        flowControlDevices,
        isLoading,
        error: error || null,
        addFlowControlDevice,
        isAdding,
        addError: addError || null,
        editFlowControlDevice,
        isEditing,
        editError: editError || null,
        deleteFlowControlDevice,
        isDeleting,
        deleteError: deleteError || null
    }
}